import { v4 as uuidv4 } from 'uuid';

import {
    isLandscape,
    isEven,
    isOdd,
    getThumbnailWidth,
    isVideo,
} from '@libs/NwUtils';

// export const playlistItemsWithMedia = (playlistItems, documentsList) => {
//     return playlistItems.map((playlistItem) => {
//         const media = documentsList.find(
//             (document) => document.ID === playlistItem.ID
//         );
//         return {
//             ...playlistItem,
//             media: media ? { ...media } : null,
//         };
//     });
// };

export const isPlaceholder = (playlistItem) => {
    return !playlistItem.media;
};

/**
 * Payload dei documenti per il salvataggio del book.
 * I placeholder non vengono inviati ma occupano comunque una posizione in
 * `DocumentOrder`: `bookInit` ricostruisce le pagine bianche dai salti nella
 * numerazione, quindi il contatore deve avanzare anche su di loro.
 */
export const buildPlaylistDocuments = (playlistItems, playlistID) => {
    const documents = [];
    let indexOrder = 0;
    for (const item of playlistItems) {
        if (!isPlaceholder(item)) {
            documents.push({
                PlaylistID: playlistID,
                DocumentID: item.ID,
                Note: null,
                DocumentOrder: indexOrder,
            });
        }
        indexOrder++;
    }
    return documents;
};

export const getPlaceholderPictureForBook = (unremovable = false) => {
    return {
        ID: uuidv4(),
        media: null,
        page: null,
        unremovable: unremovable,
        isLandscape: false,
        isVideo: false,
    };
};

export const getPlaylistItemStyle = (picture, doublepage) => {
    const mRight = doublepage
        ? isEven(picture.page)
            ? '2rem'
            : '.25rem'
        : '1rem';
    return {
        marginRight: mRight,
    };
};

export const bookInit = (book) => {
    const pictures = [];
    if (book.Layout === 'Pages') {
        let lastOrder = 0;
        for (let i = 0; i < book.DocumentPlaylists.length; i++) {
            const mypic = book.DocumentPlaylists[i];
            if (i === 0 && mypic.DocumentOrder === 1) {
                pictures.push(getPlaceholderPictureForBook());
            } else {
                if (mypic.DocumentOrder > lastOrder + 1) {
                    const nplaceholders = mypic.DocumentOrder - (lastOrder + 1);
                    for (let p = 0; p < nplaceholders; p++) {
                        pictures.push(getPlaceholderPictureForBook());
                    }
                }
            }
            pictures.push({
                ID: mypic.ID,
                page: null,
                media: mypic,
                isLandscape: isLandscape(mypic),
                isVideo: isVideo(mypic),
            });
            lastOrder = mypic.DocumentOrder;
        }
        setPageNumber(pictures);
    } else {
        const mappedDocuments = book.DocumentPlaylists.map((document) => {
            return {
                ID: document.ID,
                page: null,
                media: document,
                isLandscape: isLandscape(document),
            };
        });
        pictures.push(...mappedDocuments);
    }
    return pictures;
};

export const formatPages = (pictures, singlePage = false) => {
    let updatedPictures = [];

    if (singlePage) {
        updatedPictures = pictures
            .filter((p) => !isPlaceholder(p))
            .map((picture, index) => {
                return {
                    ...picture,
                    page: index + 1,
                };
            });
        return updatedPictures;
    }

    if (pictures && pictures.length) {
        loopForPlaceholders(pictures);
        updatedPictures = [...markPlaceholdersAsRemovable(pictures)];
    }
    return updatedPictures;
};

const setPageNumber = (pictures) => {
    let pages = 0;
    for (let i = 0; i < pictures.length; i++) {
        const mypic = pictures[i];
        pages += mypic.media && isLandscape(mypic.media) ? 2 : 1;
        mypic.page = pages;
    }
    return true;
};

const loopForPlaceholders = (pictures) => {
    setPageNumber(pictures);
    const changed = addAutoPlaceholders(pictures);
    if (changed) {
        loopForPlaceholders(pictures);
    } else {
        return true;
    }
};

const addAutoPlaceholders = (pictures) => {
    const irregularIndex = pictures.findIndex((pic) => {
        return !isPlaceholder(pic) && isLandscape(pic.media) && isOdd(pic.page);
    });
    if (irregularIndex >= 0) {
        if (irregularIndex > 0 && isPlaceholder(pictures[irregularIndex - 1])) {
            pictures.splice(irregularIndex - 1, 1);
        } else {
            pictures.splice(
                irregularIndex,
                0,
                getPlaceholderPictureForBook(true)
            );
        }
        return true;
    }
    return false;
};

const markPlaceholdersAsRemovable = (pictures) => {
    let updatedPictures = [...pictures];
    for (let i = 0; i < updatedPictures.length; i++) {
        const mypic = updatedPictures[i];
        let unrem = false;
        if (isPlaceholder(mypic)) {
            if (isEven(mypic.page)) {
                if (i < updatedPictures.length - 1) {
                    if (!isPlaceholder(updatedPictures[i + 1])) {
                        if (isLandscape(updatedPictures[i + 1].media)) {
                            unrem = true;
                        }
                    }
                }
            }
            mypic.unremovable = unrem;

            // if (
            //     i < updatedPictures.length - 1 &&
            //     isOdd(mypic.page) &&
            //     !isPlaceholder(updatedPictures[i + 1])
            // ) {
            //     mypic.unremovable = false;
            //     updatedPictures[i + 1].unremovable = false;
            //     i++;
            // } else {
            //     if (i === updatedPictures.length - 1) {
            //         mypic.unremovable = false;
            //     } else {
            //         mypic.unremovable = true;
            //     }
            // }
        }
    }
    return updatedPictures;
};

export const fixDoublePlaceholders = (pictures) => {
    let updatedPictures = [...pictures];
    const doubleplaceholders = [];
    //verify if there are 2 subsequent placeholders
    for (let i = 0; i < updatedPictures.length; i++) {
        const mypic = updatedPictures[i];
        if (isPlaceholder(mypic)) {
            if (
                i < updatedPictures.length - 1 &&
                isOdd(mypic.page) &&
                !isPlaceholder(updatedPictures[i + 1])
            ) {
                //not last picture, page is odd and next picture is not a placeholder
                //to remove, otherwise it's a full blank page
                doubleplaceholders.push(i);
            }
        }
    }
    //remove subsequent placeholders
    doubleplaceholders.forEach((item) => {
        updatedPictures.splice(item, 2);
    });
    return updatedPictures;
};
